import React, { useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

import Icon5 from '../assets/TrefleTrans.svg';
import Icon6 from '../assets/CoeurTrans.svg';
import SingleImage from '../assets/bg.png';
import Icon1 from '../assets/icon1.svg';
import Icon2 from '../assets/icon2.svg';
import Icon3 from '../assets/icon3.svg';
import Icon4 from '../assets/icon4.svg';
import C1 from "../assets/carreau.png"
import C2 from "../assets/coeur.png"
import C3 from "../assets/pique.png"
import C4 from "../assets/trefle.png"


gsap.registerPlugin(ScrollTrigger);

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  padding-bottom: 10%;
  position: relative;
  text-align: center;
  background-color: rgba(102, 15, 19, 1);
  overflow: hidden;

  @media (max-width: 900px) {
    padding: 4rem 2rem;
    min-height: auto;
  }
`;

const Title = styled.p`
  font-size: 8rem;
  font-family: 'MongoGrotesqueLight';
  margin: 0;
  color: #fff;
  display: flex;
  align-items: center;

  @media (max-width: 900px) {
    font-size: 5rem;
  }
  @media (max-width: 410px) {
    font-size: 4rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  margin: 0;
  color: white;
`;

const TitreContainer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  margin-top: 2%;
  text-align: center;
  width: 80%;

  @media (max-width: 900px) {
    margin-top: 4%;
  }
`;

const IconsRow = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const LargeIcon = styled.img`
  width: 250px;
  height: 250px;
  position: absolute;
  left: 0;
  top: 40%;

  @media (max-width: 900px) {
    display: none;
  }
`;

const LargeIcon2 = styled.img`
  width: 250px;
  height: 250px;
  position: absolute;
  right: 0;
  top: 40%;

  @media (max-width: 900px) {
    display: none;
  }
`;

const ImageStack = styled.div`
  display: grid;
  grid-template-areas: 'stack';
  place-items: center;
  margin-top: 4rem;
`;

const LayerWrapper = styled.div`
  grid-area: stack;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LayerImage = styled.img`
  display: block;
  width: 400px;
  height: 520px;

  @media (max-width: 900px) {
    width: 220px;
    height: 340px;
  }
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  gap: 10px;
`;

const Icon = styled.img`
  width: 20px;
  height: 20px;
`;

const layers = [
  { angle: -15, offset: -30 },
  { angle: -5, offset: -10 },
  { angle: 2, offset: 10 },
  { angle: 5, offset: 30 },
];

const Famille: React.FC = () => {
  // ref for the container we want to pin
  const containerRef = useRef<HTMLDivElement>(null);
  // ref for the subtitle we want as scroll trigger
  const triggerRef = useRef<HTMLParagraphElement>(null);
  const layerRefs = useRef<Array<HTMLDivElement | null>>([]);

  const images = [C1, C2, C3, C4];

  useLayoutEffect(() => {
    const container = containerRef.current;
    const trigger = triggerRef.current;
    if (!container || !trigger) return;
  
    // pin only on iPad-like devices (900px–1024px device width)
    gsap.matchMedia().add(
      "only screen and (min-device-width: 900px) and (max-device-width: 1024px)",
      () => {
        ScrollTrigger.create({
          trigger: trigger,
          start: "top top",
          end: () => `+=${window.innerHeight}`,
          pin: container,
          pinSpacing: false,
        });
      }
    );
  
    // animations on all viewports
    layers.forEach((cfg, i) => {
      const el = layerRefs.current[i];
      if (!el) return;
      gsap.fromTo(
        el,
        { xPercent: 0, rotation: 0 },
        {
          xPercent: cfg.offset,
          rotation: cfg.angle,
          ease: "none",
          scrollTrigger: {
            trigger: trigger,
            start: "top bottom",
            end: () => `+=${window.innerHeight}`,
            scrub: 0.5,
          },
        }
      );
    });
  
    return () => ScrollTrigger.getAll().forEach(t => t.kill());
  }, []);

  return (
    <PageContainer ref={containerRef}>
      <IconRow>
        <Icon src={Icon2} alt="Icon 2" />
        <Icon src={Icon3} alt="Icon 3" />
        <Icon src={Icon1} alt="Icon 1" />
        <Icon src={Icon4} alt="Icon 4" />
      </IconRow>

      <TitreContainer>
        <Title>LES FAMILLES</Title>
        <Subtitle ref={triggerRef}>
          Chaque famille à son énergie. Chaque membre sa mission.
          <br />
          À toi de choisir la tienne.
        </Subtitle>
      </TitreContainer>

      <LargeIcon src={Icon5} alt="Large icon left" />
      <LargeIcon2 src={Icon6} alt="Large icon right" />

      <ImageStack>
        {layers.map((_, idx) => (
          <LayerWrapper
            key={idx}
            ref={el => {
              layerRefs.current[idx] = el;
            }}
          >
            <LayerImage src={images[idx]} alt={`Layer ${idx + 1}`} />
          </LayerWrapper>
        ))}
      </ImageStack>
    </PageContainer>
  );
};

export default Famille;